// src/store/modules/events.ts
import { db } from "@/firebaseConfig";
import type { ActionContext, Module } from "vuex";
import type {
  Event,
  EventComment,
  EventFile,
  EventScheduleItem,
} from "@/models/interfaces";
import {
  Timestamp,
  arrayUnion,
  collection,
  getDocs,
  query,
  where,
  limit,
  orderBy,
  getDoc,
  doc,
  addDoc,
  onSnapshot,
  serverTimestamp,
  deleteDoc,
  writeBatch,
  updateDoc,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import {
  defaultScheduleItemDuration,
  type TextScheduleItemType,
} from "@/utils/scheduleItemTypes";

export interface EventsState {
  fetching: { value: boolean };
  fetchingUpcomingEvent: { value: boolean };
  fetchingOngoingEvent: { value: boolean };
  fetchingSchedule: { value: boolean };
  error: string;
  events: { data: Event[] };
  upcomingEvent: Event | null;
  ongoingEvent: Event | null;
  activeEvent: Event | null;
  activeEventUnsub: (() => void) | null;
  activeSchedule: EventScheduleItem[];
  accountId: number | null;
}

const initialState = (): EventsState => ({
  fetching: { value: false },
  fetchingUpcomingEvent: { value: false },
  fetchingOngoingEvent: { value: false },
  fetchingSchedule: { value: false },
  error: "",
  events: { data: [] },
  upcomingEvent: null,
  ongoingEvent: null,
  activeEventUnsub: null,
  activeEvent: null,
  activeSchedule: [],
  accountId: null,
});

type RootState = unknown;

const sortByStartAsc = (arr: any[]) =>
  arr.sort((a, b) => {
    const av = a?.start?.toDate ? a.start.toDate() : a?.start;
    const bv = b?.start?.toDate ? b.start.toDate() : b?.start;
    return new Date(av ?? 0).getTime() - new Date(bv ?? 0).getTime();
  });

const normalizeEventFile = (raw: unknown): EventFile | null => {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Partial<EventFile> & {
    uploadedAt?: Date | Timestamp | { toDate?: () => Date } | null;
  };
  if (
    typeof obj.id !== "string" ||
    typeof obj.name !== "string" ||
    typeof obj.url !== "string"
  ) {
    return null;
  }
  const out: EventFile = {
    id: obj.id,
    name: obj.name,
    url: obj.url,
    contentType: obj.contentType ?? null,
    size: obj.size ?? null,
    storagePath: obj.storagePath ?? null,
    uploadedAt: obj.uploadedAt ?? null,
    uploadedBy: obj.uploadedBy ?? null,
  };
  if ((out.uploadedAt as any)?.toDate) {
    out.uploadedAt = (out.uploadedAt as any).toDate();
  }
  return out;
};

const normalizeEventFiles = (rawFiles: unknown): EventFile[] => {
  if (!Array.isArray(rawFiles)) return [];
  return rawFiles
    .map((raw) => normalizeEventFile(raw))
    .filter((file): file is EventFile => !!file);
};

const normalizeEventComment = (raw: unknown): EventComment | null => {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Partial<EventComment> & {
    timestamp?: Date | Timestamp | { toDate?: () => Date } | null;
  };
  if (typeof obj.id !== "string" || typeof obj.comment !== "string") {
    return null;
  }
  const out: EventComment = {
    id: obj.id,
    userId: obj.userId == null ? null : String(obj.userId),
    name: String(obj.name ?? ""),
    thumbnail: String(obj.thumbnail ?? ""),
    comment: obj.comment,
    timestamp: obj.timestamp ?? null,
  };
  if ((out.timestamp as any)?.toDate) {
    out.timestamp = (out.timestamp as any).toDate();
  }
  return out;
};

const normalizeEventComments = (rawComments: unknown): EventComment[] => {
  if (!Array.isArray(rawComments)) return [];
  return rawComments
    .map((raw) => normalizeEventComment(raw))
    .filter((comment): comment is EventComment => !!comment);
};

const normalizeEvent = (id: string, data: any): Event => {
  const out = { ...data, id };
  if (out.start?.toDate) out.start = out.start.toDate();
  if (out.end?.toDate) out.end = out.end.toDate();
  out.files = normalizeEventFiles(out.files);
  out.comments = normalizeEventComments(out.comments);
  return out as Event;
};

/** When an event ends: an event without an end is taken to last two hours, as in the apps. */
const eventEnd = (evt: Event): Date =>
  evt.end ?? new Date(new Date(evt.start as any).getTime() + 2 * 60 * 60 * 1000);

const extractTeamId = (value: unknown): string | null => {
  if (!value) return null;

  if (typeof value === "string") {
    const parts = value.split("/");
    return parts[parts.length - 1] || null;
  }

  if (typeof value === "object") {
    const obj = value as any;

    if (typeof obj.id === "string") return obj.id;

    if (typeof obj.path === "string") {
      const parts = obj.path.split("/");
      return parts[parts.length - 1] || null;
    }

    const segments = obj?._key?.path?.segments;
    if (Array.isArray(segments) && segments.length > 0) {
      return segments[segments.length - 1] ?? null;
    }
  }

  return null;
};

const getUserTeamIdSet = (rootGetters: any): Set<string> => {
  const user = rootGetters?.["users/user"];
  const teams = Array.isArray(user?.teams) ? user.teams : [];

  return new Set(
    teams
      .map((team: unknown): string | null => extractTeamId(team))
      .filter((teamId: string | null): teamId is string => !!teamId),
  );
};

const getCurrentUserIdSet = (rootGetters: any): Set<string> => {
  const user = rootGetters?.["users/user"];
  const ids = [user?.uid, user?.id]
    .map((id) => (id == null ? null : String(id)))
    .filter((id): id is string => !!id);
  return new Set(ids);
};

const canUserSeeEvent = async (
  evt: Event | null | undefined,
  currentUserIds: Set<string>,
  userTeamIds: Set<string>,
): Promise<boolean> => {
  if (!evt?.id) return false;

  // Default visibility: null/undefined is treated as public.
  if (evt.isPublic == null || evt.isPublic === true) return true;

  // Private events are always visible to the user who made them private.
  if (evt.privateByUserId && currentUserIds.has(String(evt.privateByUserId))) {
    return true;
  }
  // Backward-compatible fallback for old docs where creator should still see it.
  if (evt.createdBy && currentUserIds.has(String(evt.createdBy))) {
    return true;
  }

  if (userTeamIds.size === 0) return false;

  const teamsSnap = await getDocs(collection(db, "events", evt.id, "teams"));
  return teamsSnap.docs.some((teamDoc) => userTeamIds.has(teamDoc.id));
};

const filterVisibleEventsForUser = async (
  rows: Event[],
  rootGetters: any,
): Promise<Event[]> => {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const currentUserIds = getCurrentUserIdSet(rootGetters);
  const userTeamIds = getUserTeamIdSet(rootGetters);

  const visibility = await Promise.all(
    rows.map(async (evt) => ({
      evt,
      canSee: await canUserSeeEvent(evt, currentUserIds, userTeamIds),
    })),
  );

  return visibility.filter((v) => v.canSee).map((v) => v.evt);
};

const teamsModule: Module<EventsState, RootState> = {
  namespaced: true,

  state: initialState,

  getters: {
    isFetching: (state) => !!state.fetching.value,
    isFetchingUpcomingEvent: (state) => !!state.fetchingUpcomingEvent.value,
    isFetchingOngoingEvent: (state) => !!state.fetchingOngoingEvent.value,
    isFetchingSchedule: (state) => !!state.fetchingSchedule.value,
    events: (state) => state.events.data,
    activeEvent: (state) => state.activeEvent,
    upcomingEvent: (state) => state.upcomingEvent,
    ongoingEvent: (state) => state.ongoingEvent,
    activeSchedule: (state) => state.activeSchedule,
    accountId: (state) => state.accountId,
  },

  mutations: {
    SET_FETCHING(state, v: unknown) {
      state.fetching.value = !!v;
    },
    SET_ERROR(state, msg?: string) {
      state.error = msg || "";
    },
    SET_EVENTS(state, rows: Event[]) {
      state.events.data = Array.isArray(rows) ? rows : [];
    },
    SET_FETCHING_UPCOMING_EVENT(state, v: unknown) {
      state.fetchingUpcomingEvent.value = !!v;
    },
    SET_FETCHING_ONGOING_EVENT(state, v: unknown) {
      state.fetchingOngoingEvent.value = !!v;
    },
    SET_UPCOMING_EVENT(state, evt: Event | null) {
      state.upcomingEvent = evt;
    },
    SET_ONGOING_EVENT(state, evt: Event | null) {
      state.ongoingEvent = evt;
    },
    SET_ACTIVE_EVENT(state, evt: Event | null) {
      state.activeEvent = evt;
    },
    SET_ACTIVE_EVENT_UNSUB(state, fn: (() => void) | null) {
      state.activeEventUnsub = fn;
    },
    CLEAR_ACTIVE_EVENT_UNSUB(state) {
      if (state.activeEventUnsub) {
        state.activeEventUnsub();
        state.activeEventUnsub = null;
      }
    },

    // ===== Schedule mutations =====
    SET_FETCHING_SCHEDULE(state, v: unknown) {
      state.fetchingSchedule.value = !!v;
    },
    SET_ACTIVE_SCHEDULE(state, items: any[]) {
      state.activeSchedule = Array.isArray(items) ? sortByStartAsc(items) : [];
    },
    SET_ACTIVE_SCHEDULE_ORDER(state, items: EventScheduleItem[]) {
      state.activeSchedule = Array.isArray(items) ? [...items] : [];
    },
    CLEAR_ACTIVE_SCHEDULE(state) {
      state.activeSchedule = [];
    },
    SORT_ACTIVE_SCHEDULE(state) {
      state.activeSchedule = sortByStartAsc([...state.activeSchedule]);
    },
    UPDATE_SCHEDULE_ITEM(state, payload) {
      const idx = state.activeSchedule.findIndex(
        (i: any) => i?.id === payload.scheduleId,
      );
      if (idx === -1) return;

      state.activeSchedule[idx] = {
        ...state.activeSchedule[idx],
        ...payload.data,
      };

      if (Object.prototype.hasOwnProperty.call(payload.data, "start")) {
        state.activeSchedule = sortByStartAsc([...state.activeSchedule]);
      }
    },
    APPEND_ACTIVE_SCHEDULE_ITEM(state, item: EventScheduleItem) {
      state.activeSchedule = sortByStartAsc([...state.activeSchedule, item]);
    },
    APPEND_ACTIVE_SCHEDULE_ITEMS(state, items: EventScheduleItem[]) {
      state.activeSchedule = sortByStartAsc([
        ...state.activeSchedule,
        ...items,
      ]);
    },
    REPLACE_ACTIVE_SCHEDULE_ITEM(
      state,
      payload: { id: string; item: EventScheduleItem },
    ) {
      const idx = state.activeSchedule.findIndex(
        (i: any) => i?.id === payload.id,
      );
      if (idx !== -1) {
        const copy = state.activeSchedule.slice();
        copy.splice(idx, 1, payload.item);
        state.activeSchedule = sortByStartAsc(copy);
      }
    },
    REMOVE_ACTIVE_SCHEDULE_ITEM(state, id: string) {
      state.activeSchedule = state.activeSchedule.filter(
        (i: any) => i?.id !== id,
      );
    },

    // ===== Events list maintenance =====
    REMOVE_EVENT_FROM_LIST(state, eventId: string) {
      state.events.data = state.events.data.filter((e) => e.id !== eventId);
    },
    SET_EVENT_PUBLIC(
      state,
      payload: {
        eventId: string;
        isPublic: boolean;
        privateByUserId?: string | null;
      },
    ) {
      const apply = (evt: Event | null): Event | null =>
        evt?.id === payload.eventId
          ? {
              ...evt,
              isPublic: payload.isPublic,
              privateByUserId: payload.privateByUserId ?? null,
            }
          : evt;

      state.activeEvent = apply(state.activeEvent);
      state.upcomingEvent = apply(state.upcomingEvent);
      state.ongoingEvent = apply(state.ongoingEvent);
      state.events.data = state.events.data.map((evt) =>
        evt.id === payload.eventId
          ? {
              ...evt,
              isPublic: payload.isPublic,
              privateByUserId: payload.privateByUserId ?? null,
            }
          : evt,
      );
    },
    UPSERT_EVENT_FILE(
      state,
      payload: {
        eventId: string;
        file: EventFile;
      },
    ) {
      const mergeFile = (files: EventFile[] | undefined): EventFile[] => {
        const list = Array.isArray(files) ? [...files] : [];
        const index = list.findIndex((item) => item?.id === payload.file.id);
        if (index === -1) {
          list.unshift(payload.file);
        } else {
          list.splice(index, 1, payload.file);
        }
        return list;
      };

      const apply = (evt: Event | null): Event | null => {
        if (!evt || evt.id !== payload.eventId) return evt;
        return {
          ...evt,
          files: mergeFile(evt.files),
        };
      };

      state.activeEvent = apply(state.activeEvent);
      state.upcomingEvent = apply(state.upcomingEvent);
      state.ongoingEvent = apply(state.ongoingEvent);
      state.events.data = state.events.data.map((evt) =>
        evt.id === payload.eventId
          ? {
              ...evt,
              files: mergeFile(evt.files),
            }
          : evt,
      );
    },
    REMOVE_EVENT_FILE(
      state,
      payload: {
        eventId: string;
        fileId: string;
      },
    ) {
      const apply = (evt: Event | null): Event | null => {
        if (!evt || evt.id !== payload.eventId) return evt;
        return {
          ...evt,
          files: (evt.files ?? []).filter((file) => file?.id !== payload.fileId),
        };
      };

      state.activeEvent = apply(state.activeEvent);
      state.upcomingEvent = apply(state.upcomingEvent);
      state.ongoingEvent = apply(state.ongoingEvent);
      state.events.data = state.events.data.map((evt) =>
        evt.id === payload.eventId
          ? {
              ...evt,
              files: (evt.files ?? []).filter(
                (file) => file?.id !== payload.fileId,
              ),
            }
          : evt,
      );
    },
    UPSERT_EVENT_COMMENT(
      state,
      payload: {
        eventId: string;
        comment: EventComment;
      },
    ) {
      const mergeComment = (
        comments: EventComment[] | undefined,
      ): EventComment[] => {
        const list = Array.isArray(comments) ? [...comments] : [];
        const index = list.findIndex((item) => item?.id === payload.comment.id);
        if (index === -1) {
          list.unshift(payload.comment);
        } else {
          list.splice(index, 1, payload.comment);
        }
        return list;
      };

      const apply = (evt: Event | null): Event | null => {
        if (!evt || evt.id !== payload.eventId) return evt;
        return {
          ...evt,
          comments: mergeComment(evt.comments),
        };
      };

      state.activeEvent = apply(state.activeEvent);
      state.upcomingEvent = apply(state.upcomingEvent);
      state.ongoingEvent = apply(state.ongoingEvent);
      state.events.data = state.events.data.map((evt) =>
        evt.id === payload.eventId
          ? {
              ...evt,
              comments: mergeComment(evt.comments),
            }
          : evt,
      );
    },

    RESET_STATE(state) {
      Object.assign(state, initialState());
    },
    SET_ACCOUNT_ID(state, accountId: number | null) {
      state.accountId = accountId ?? null;
    },
  },

  actions: {
    // ===== Your original helper =====
    async setAccount({ commit }, accountId: number | null) {
      commit("SET_ACCOUNT_ID", accountId);
    },

    // ===== Upcoming event =====
    async fetchUpcomingEvent({
      state,
      commit,
      rootGetters,
    }: ActionContext<EventsState, RootState>): Promise<{
      result: string | void | null;
    }> {
      if (state.fetchingUpcomingEvent.value) {
        return { result: "failed" };
      }
      if (state.accountId == null) {
        commit("SET_UPCOMING_EVENT", null);
        return { result: "failed" };
      }
      commit("SET_FETCHING_UPCOMING_EVENT", true);
      try {
        const now = new Date();
        const col = collection(db, "events");

        try {
          const q = query(
            col,
            where("account", "==", state.accountId),
            where("start", ">", now),
            orderBy("start", "asc"),
            limit(25),
          );
          const snapshot = await getDocs(q);

          if (snapshot.empty) {
            commit("SET_UPCOMING_EVENT", null);
            return { result: "success" };
          }

          const candidateEvents = snapshot.docs.map((d) =>
            normalizeEvent(d.id, d.data()),
          );
          const visibleEvents = await filterVisibleEventsForUser(
            candidateEvents,
            rootGetters,
          );
          const firstVisibleUpcoming = visibleEvents[0] ?? null;
          if (firstVisibleUpcoming) {
            commit("SET_UPCOMING_EVENT", firstVisibleUpcoming);
            return { result: "success" };
          }
        } catch (err) {
          // Fallback for environments missing the composite index.
          console.warn(
            "fetchUpcomingEvent indexed query failed, using fallback",
            err,
          );
        }

        const fallbackQ = query(
          col,
          where("account", "==", state.accountId),
          where("start", ">", now),
        );
        const fallbackSnap = await getDocs(fallbackQ);
        const sortedUpcoming = fallbackSnap.docs
          .map((d) => normalizeEvent(d.id, d.data()))
          .sort(
            (a, b) =>
              new Date(a.start as any).getTime() -
              new Date(b.start as any).getTime(),
          );
        const visibleEvents = await filterVisibleEventsForUser(
          sortedUpcoming,
          rootGetters,
        );
        commit("SET_UPCOMING_EVENT", visibleEvents[0] ?? null);
        return { result: "success" };
      } finally {
        commit("SET_FETCHING_UPCOMING_EVENT", false);
      }
    },

    async fetchOngoingEvent({
      state,
      commit,
      rootGetters,
    }: ActionContext<EventsState, RootState>): Promise<{
      result: string | void | null;
    }> {
      if (state.fetchingOngoingEvent.value) {
        return { result: "failed" };
      }
      if (state.accountId == null) {
        commit("SET_ONGOING_EVENT", null);
        return { result: "failed" };
      }
      commit("SET_FETCHING_ONGOING_EVENT", true);

      try {
        const now = new Date();
        const col = collection(db, "events");

        // The events started most recently, rather than a query on `end`,
        // which would never find an event without one.
        try {
          const startedQ = query(
            col,
            where("account", "==", state.accountId),
            where("start", "<=", now),
            orderBy("start", "desc"),
            limit(25),
          );
          const startedSnap = await getDocs(startedQ);
          const nowMs = now.getTime();
          const ongoingCandidates = startedSnap.docs
            .map((d) => normalizeEvent(d.id, d.data()))
            .filter((evt) => {
              const endMs = eventEnd(evt).getTime();
              return Number.isFinite(endMs) && endMs >= nowMs;
            });
          const visibleEvents = await filterVisibleEventsForUser(
            ongoingCandidates,
            rootGetters,
          );
          commit("SET_ONGOING_EVENT", visibleEvents[0] ?? null);
          return { result: "success" };
        } catch (err: any) {
          const msg = String(err?.message || err || "");
          const url = msg.match(/https?:\/\/\S+/)?.[0];
          console.error("Firestore index URL:", url);
          console.error("Full Firestore error:", msg);
          commit("SET_ONGOING_EVENT", null);
          return { result: "failed" };
        }
        commit("SET_ONGOING_EVENT", null);
        return { result: "success" };
      } finally {
        commit("SET_FETCHING_ONGOING_EVENT", false);
      }
    },

    // ===== Active event (kept) =====
    async fetchEventById(
      { commit, state }: ActionContext<EventsState, RootState>,
      id: string,
    ) {
      if (state.activeEventUnsub) {
        commit("CLEAR_ACTIVE_EVENT_UNSUB");
      }

      // Clear any existing schedule when switching events
      commit("CLEAR_ACTIVE_SCHEDULE");
      commit("SET_FETCHING", true);

      const ref = doc(db, "events", id);

      const unsubscribe = onSnapshot(ref, (snap) => {
        if (!snap.exists()) {
          commit("SET_ACTIVE_EVENT", null);
          commit("SET_FETCHING", false);
          return;
        }

        const data = snap.data() as any;
        if (data.start?.toDate) data.start = data.start.toDate();
        if (data.end?.toDate) data.end = data.end.toDate();
        data.files = normalizeEventFiles(data.files);
        data.comments = normalizeEventComments(data.comments);

        const obj = { id: snap.id, ...data } as Event;

        commit("SET_ACTIVE_EVENT", obj);
        commit("SET_FETCHING", false);
      });

      commit("SET_ACTIVE_EVENT_UNSUB", unsubscribe);
    },

    async setEventPublic(
      { commit, rootGetters }: ActionContext<EventsState, RootState>,
      payload: { eventId: string; isPublic: boolean | null | undefined },
    ) {
      const eventId = String(payload?.eventId ?? "");
      if (!eventId) return;

      const isPublic = payload?.isPublic == null ? true : !!payload.isPublic;
      const actorId = rootGetters?.["users/user"]?.uid
        ? String(rootGetters["users/user"].uid)
        : rootGetters?.["users/user"]?.id
          ? String(rootGetters["users/user"].id)
          : null;
      const eventRef = doc(db, "events", eventId);
      const eventSnap = await getDoc(eventRef);
      if (!eventSnap.exists()) {
        throw new Error("Event not found");
      }
      const createdByRaw = (eventSnap.data() as Event)?.createdBy ?? null;
      const createdBy = createdByRaw == null ? null : String(createdByRaw);
      if (!actorId || !createdBy || actorId !== createdBy) {
        throw new Error("Only the event creator can change event visibility");
      }

      const privateByUserId = isPublic ? null : actorId;

      await updateDoc(eventRef, {
        isPublic,
        privateByUserId,
      });
      commit("SET_EVENT_PUBLIC", { eventId, isPublic, privateByUserId });
    },

    async uploadEventFile(
      { commit, rootGetters }: ActionContext<EventsState, RootState>,
      payload: { eventId: string; file: File },
    ): Promise<EventFile> {
      const eventId = String(payload?.eventId ?? "");
      const file = payload?.file ?? null;
      if (!eventId) throw new Error("Missing event id");
      if (!file) throw new Error("Missing file");

      const user = rootGetters?.["users/user"];
      const userType = Number(user?.usertype ?? 0);
      if (userType < 2) {
        throw new Error("Only admins can upload event files");
      }

      const eventRef = doc(db, "events", eventId);
      const eventSnap = await getDoc(eventRef);
      if (!eventSnap.exists()) {
        throw new Error("Event not found");
      }

      const storage = getStorage();
      const fileId =
        typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const safeName = file.name
        .trim()
        .replace(/[^\w.-]+/g, "_")
        .replace(/_+/g, "_")
        .slice(0, 120);
      const storagePath = `event-files/${eventId}/${fileId}-${safeName || "file"}`;
      const fileRef = storageRef(storage, storagePath);

      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);

      const uploadedAt = Timestamp.now();
      const uploadedBy = user?.uid
        ? String(user.uid)
        : user?.id
          ? String(user.id)
          : null;

      const eventFile: EventFile = {
        id: fileId,
        name: file.name || "file",
        url,
        contentType: file.type || null,
        size: Number.isFinite(file.size) ? file.size : null,
        storagePath,
        uploadedAt,
        uploadedBy,
      };

      await updateDoc(eventRef, {
        files: arrayUnion(eventFile),
        updatedAt: serverTimestamp(),
      });

      const normalizedFile = normalizeEventFile(eventFile);
      if (normalizedFile) {
        commit("UPSERT_EVENT_FILE", {
          eventId,
          file: normalizedFile,
        });
      }

      return eventFile;
    },

    async deleteEventFile(
      { commit, rootGetters }: ActionContext<EventsState, RootState>,
      payload: { eventId: string; fileId: string },
    ): Promise<void> {
      const eventId = String(payload?.eventId ?? "");
      const fileId = String(payload?.fileId ?? "");
      if (!eventId) throw new Error("Missing event id");
      if (!fileId) throw new Error("Missing file id");

      const user = rootGetters?.["users/user"];
      const userType = Number(user?.usertype ?? 0);
      if (userType < 2) {
        throw new Error("Only admins can delete event files");
      }

      const eventRef = doc(db, "events", eventId);
      const eventSnap = await getDoc(eventRef);
      if (!eventSnap.exists()) {
        throw new Error("Event not found");
      }

      const eventData = eventSnap.data() as { files?: unknown[] };
      const rawFiles = Array.isArray(eventData?.files) ? eventData.files : [];
      const normalizedFiles = normalizeEventFiles(rawFiles);
      const fileToDelete =
        normalizedFiles.find((file) => file.id === fileId) ?? null;
      const nextRawFiles = rawFiles.filter(
        (file) => String((file as any)?.id ?? "") !== fileId,
      );

      await updateDoc(eventRef, {
        files: nextRawFiles,
        updatedAt: serverTimestamp(),
      });

      if (fileToDelete?.storagePath) {
        try {
          const storage = getStorage();
          await deleteObject(storageRef(storage, fileToDelete.storagePath));
        } catch (error) {
          console.warn("Failed to delete event file from storage:", error);
        }
      }

      commit("REMOVE_EVENT_FILE", { eventId, fileId });
    },

    async addEventComment(
      { commit, rootGetters }: ActionContext<EventsState, RootState>,
      payload: { eventId: string; comment: string },
    ): Promise<EventComment> {
      const eventId = String(payload?.eventId ?? "");
      const commentText = String(payload?.comment ?? "").trim();
      if (!eventId) throw new Error("Missing event id");
      if (!commentText) throw new Error("Missing comment");

      const user = rootGetters?.["users/user"];
      const commentId =
        typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const userId = user?.uid
        ? String(user.uid)
        : user?.id
          ? String(user.id)
          : null;
      const eventRef = doc(db, "events", eventId);
      const eventSnap = await getDoc(eventRef);
      if (!eventSnap.exists()) {
        throw new Error("Event not found");
      }

      const eventComment: EventComment = {
        id: commentId,
        userId,
        name: String(user?.name ?? user?.displayName ?? ""),
        thumbnail: String(user?.thumbnail ?? user?.photoURL ?? ""),
        comment: commentText,
        timestamp: Timestamp.now(),
      };

      await updateDoc(eventRef, {
        comments: arrayUnion(eventComment),
        updatedAt: serverTimestamp(),
      });

      const normalizedComment = normalizeEventComment(eventComment);
      if (normalizedComment) {
        commit("UPSERT_EVENT_COMMENT", {
          eventId,
          comment: normalizedComment,
        });
      }

      return eventComment;
    },

    // ===== Month fetch (kept) =====
    async fetchByMonth(
      { state, commit, rootGetters }: ActionContext<EventsState, RootState>,
      month: Date | null,
    ): Promise<{ success: true | false | void }> {
      if (state.accountId == null) {
        console.warn("events.fetchByMonth() has no accountId");
        commit("SET_EVENTS", []);
        return { success: false };
      }
      if (!month) {
        commit("SET_EVENTS", []);
        return { success: false };
      }

      const startDate = new Date(month);
      startDate.setDate(1);
      startDate.setHours(0, 0, 0, 0);

      const endDate = new Date(
        month.getFullYear(),
        month.getMonth() + 1,
        0,
        23,
        59,
        59,
        999,
      );

      const q = query(
        collection(db, "events"),
        where("account", "==", state.accountId),
        where("start", ">", startDate),
        where("start", "<", endDate),
      );
      const snap = await getDocs(q);

      const result: Event[] = snap.docs.map((d) => {
        const data = d.data() as any;
        data.id = d.id;
        if (data.start?.toDate) data.start = data.start.toDate();
        if (data.end?.toDate) data.end = data.end.toDate();
        data.files = normalizeEventFiles(data.files);
        data.comments = normalizeEventComments(data.comments);
        return data as Event;
      });
      const visibleEvents = await filterVisibleEventsForUser(
        result,
        rootGetters,
      );
      commit("SET_EVENTS", visibleEvents);

      return { success: true };
    },

    // ===== Schedule fetch (sorted) =====
    async fetchSchedule(
      { commit }: ActionContext<EventsState, RootState>,
      payload: { docId: string; callback?: () => void },
    ) {
      commit("SET_FETCHING_SCHEDULE", true);
      try {
        const collectionRef = collection(
          db,
          "events",
          payload.docId,
          "schedule",
        );
        const snapshot = await getDocs(collectionRef);
        const schedule = snapshot.docs.map((d) => {
          const data = d.data() as any;
          if (data?.timestamp?.toDate) data.timestamp = data.timestamp.toDate();
          if (data?.start?.toDate) data.start = data.start.toDate();
          return { id: d.id, ...data };
        });
        commit("SET_ACTIVE_SCHEDULE", schedule); // already sorts
      } finally {
        commit("SET_FETCHING_SCHEDULE", false);
        if (payload.callback) payload.callback();
      }
    },

    // ===== Private helper: add + append + reconcile =====
    async _addItemToScheduleAndAppend(
      { state, commit },
      payload: {
        docId: string;
        toWrite: Omit<EventScheduleItem, "id"> & { timestamp?: any };
        afterWrite?: (id: string) => void;
      },
    ) {
      const parentRef = doc(db, "events", payload.docId);
      const colRef = collection(parentRef, "schedule");

      // 1) Write to Firestore
      const newRef = await addDoc(colRef, {
        ...payload.toWrite,
        timestamp: serverTimestamp(),
      });

      // 2) Optimistic append (if we're on this event)
      if (state.activeEvent?.id === payload.docId) {
        const optimisticItem = {
          id: newRef.id,
          ...payload.toWrite,
          timestamp: new Date(),
        } as unknown as EventScheduleItem;
        commit("APPEND_ACTIVE_SCHEDULE_ITEM", optimisticItem);
      }

      // 3) Optional callback
      if (payload.afterWrite) payload.afterWrite(newRef.id);

      // 4) Reconcile with server data (serverTimestamp resolved)
      try {
        const fresh = await getDoc(newRef);
        if (fresh.exists() && state.activeEvent?.id === payload.docId) {
          const data = fresh.data() as any;
          if (data?.timestamp?.toDate) data.timestamp = data.timestamp.toDate();
          if (data?.start?.toDate) data.start = data.start.toDate();
          commit("REPLACE_ACTIVE_SCHEDULE_ITEM", {
            id: newRef.id,
            item: { id: newRef.id, ...data } as EventScheduleItem,
          });
        }
      } catch {
        /* no-op; optimistic item remains */
      }
    },

    async addScheduleItems(
      { state, commit },
      payload: {
        docId: string;
        items: Array<Omit<EventScheduleItem, "id">>;
        /** Fields to set on the event itself in the same write. */
        eventFields?: Record<string, unknown>;
      },
    ) {
      if (!payload?.docId || !Array.isArray(payload.items)) return;

      const parentRef = doc(db, "events", payload.docId);
      const colRef = collection(parentRef, "schedule");
      const batch = writeBatch(db);
      if (payload.eventFields) batch.update(parentRef, payload.eventFields);
      const optimisticItems: EventScheduleItem[] = [];

      for (const item of payload.items) {
        const newRef = doc(colRef);
        batch.set(newRef, {
          ...item,
          timestamp: serverTimestamp(),
        });
        optimisticItems.push({
          id: newRef.id,
          ...item,
          timestamp: new Date(),
        } as unknown as EventScheduleItem);
      }

      await batch.commit();

      if (state.activeEvent?.id === payload.docId) {
        commit("APPEND_ACTIVE_SCHEDULE_ITEMS", optimisticItems);
      }
    },

    // ===== Public: add sermon =====
    async addSermonToSchedule(
      { dispatch },
      payload: {
        docId: string;
        type: "sermon";
        start?: any; // Timestamp | Date | string depending on your app
        data: { title: string; preacher?: string; description?: string };
        callback?: () => void;
      },
    ) {
      const toWrite = {
        type: payload.type,
        start: payload.start ?? null,
        title: payload.data.title,
        preacher: payload.data.preacher ?? null,
        description: payload.data.description ?? null,
      } as unknown as Omit<EventScheduleItem, "id">;

      await dispatch("_addItemToScheduleAndAppend", {
        docId: payload.docId,
        toWrite,
        afterWrite: () => payload.callback?.(),
      });
    },

    // ===== Public: add feature =====
    async addFeatureToSchedule(
      { dispatch },
      payload: {
        docId: string;
        type?: TextScheduleItemType;
        title: string;
        description?: string;
        subject?: string;
        mobilePayNumber?: string;
        start?: any; // Timestamp | Date | string depending on your app
        duration?: number;
        callback?: () => void;
      },
    ) {
      const type = payload.type ?? "feature";
      const toWrite = {
        type,
        start: payload.start ?? null,
        title: payload.title,
        description: payload.description ?? null,
        duration: payload.duration ?? defaultScheduleItemDuration(type),
        ...(type === "collect"
          ? {
              subject: payload.subject ?? null,
              mobilePayNumber: payload.mobilePayNumber ?? null,
            }
          : {}),
      } as unknown as Omit<EventScheduleItem, "id">;

      await dispatch("_addItemToScheduleAndAppend", {
        docId: payload.docId,
        toWrite,
        afterWrite: () => payload.callback?.(),
      });
    },

    // ===== Public: add slides =====
    async addSlidesToSchedule(
      { dispatch },
      payload: {
        docId: string;
        /** A slideshow in the organisation's library. */
        slideshowId: string;
        title: string;
        start?: any; // Timestamp | Date | string depending on your app
        duration?: number;
      },
    ) {
      const toWrite = {
        type: "slides",
        start: payload.start ?? null,
        title: payload.title,
        duration: payload.duration ?? defaultScheduleItemDuration("slides"),
        slideshowId: payload.slideshowId,
      } as unknown as Omit<EventScheduleItem, "id">;

      await dispatch("_addItemToScheduleAndAppend", {
        docId: payload.docId,
        toWrite,
      });
    },

    async updateScheduleItem(
      { commit, state },
      payload: { eventId: string; scheduleId: string; data: any },
    ) {
      const docRef = doc(
        db,
        "events",
        payload.eventId,
        "schedule",
        payload.scheduleId,
      );
      try {
        const obj: Record<string, unknown> = {};
        if (payload.data.key !== undefined) {
          obj.key = payload.data.key;
        }
        if (payload.data.start !== undefined) {
          obj.start = payload.data.start;
        }
        if (payload.data.duration !== undefined) {
          obj.duration = payload.data.duration;
        }
        if (payload.data.arrangement !== undefined) {
          obj.arrangement = payload.data.arrangement;
        }
        // The comments on plays of the row's arrangement go with it
        // (`utils/arrangementComments.ts`).
        if (payload.data.arrangementComments !== undefined) {
          obj.arrangementComments = payload.data.arrangementComments;
        }
        if (payload.data.lead !== undefined) {
          obj.lead = payload.data.lead;
        }
        if (payload.data.description !== undefined) {
          obj.description = payload.data.description;
        }
        if (payload.data.title !== undefined) {
          obj.title = payload.data.title;
        }
        if (payload.data.subject !== undefined) {
          obj.subject = payload.data.subject;
        }
        if (payload.data.mobilePayNumber !== undefined) {
          obj.mobilePayNumber = payload.data.mobilePayNumber;
        }
        if (payload.data.slideshowId !== undefined) {
          obj.slideshowId = payload.data.slideshowId;
        }
        if (payload.data.variantName !== undefined) {
          obj.variantName = payload.data.variantName;
        }
        if (payload.data.songId !== undefined) {
          obj.songId = payload.data.songId;
          obj.reference = payload.data.songId
            ? doc(db, "songs", String(payload.data.songId))
            : null;
        }
        if (payload.data.reference !== undefined) {
          obj.reference = payload.data.reference;
        }
        if (!Object.keys(obj).length) return;

        await updateDoc(docRef, obj);
        commit("UPDATE_SCHEDULE_ITEM", {
          ...payload,
          data: obj,
        });
        // context.commit("UPDATE_ACTIVE_SCHEDULE_SONG_KEY", payload)
      } catch (error) {
        console.error("Error while updating song: ", error);
      }
    },

    async reorderSchedule(
      { commit },
      payload: { eventId: string; items: EventScheduleItem[] },
    ) {
      if (!payload?.eventId || !Array.isArray(payload.items)) return;

      try {
        const batch = writeBatch(db);

        for (const item of payload.items) {
          if (!item?.id) continue;
          const ref = doc(db, "events", payload.eventId, "schedule", item.id);
          batch.update(ref, { start: item.start });
        }

        await batch.commit();
        commit("SET_ACTIVE_SCHEDULE_ORDER", payload.items);
      } catch (error) {
        console.error("Error while reordering schedule: ", error);
        throw error;
      }
    },

    // ===== Public: add song =====
    async addSongToSchedule(
      { dispatch },
      payload: {
        docId: string;
        start?: any; // Timestamp | Date | string depending on your app
        data: {
          title: string;
          songId?: string;
          id?: string;
          key?: string;
          duration?: number;
          /** Set when the song is a version of another, not the original. */
          variantName?: string | null;
        };
        songId: string; // explicit
        callback?: () => void;
      },
    ) {
      const songRef = doc(db, "songs", payload.songId);
      const toWrite = {
        type: "song",
        start: payload.start ?? null,
        title: payload.data.title,
        duration: payload.data.duration ?? defaultScheduleItemDuration("song"),
        songId: payload.data.songId ?? payload.data.id ?? payload.songId,
        key: payload.data.key ?? null,
        reference: songRef,
        variantName: payload.data.variantName ?? null,
      } as unknown as Omit<EventScheduleItem, "id">;

      await dispatch("_addItemToScheduleAndAppend", {
        docId: payload.docId,
        toWrite,
        afterWrite: () => payload.callback?.(),
      });
    },

    // ===== Public: remove schedule item (optimistic) =====
    async removeScheduleItem(
      { commit, state },
      payload: { eventId: string; itemId: string; callback?: () => void },
    ) {
      try {
        // Optimistic local removal
        commit("REMOVE_ACTIVE_SCHEDULE_ITEM", payload.itemId);

        // Firestore deletion
        const ref = doc(
          db,
          "events",
          payload.eventId,
          "schedule",
          payload.itemId,
        );
        await deleteDoc(ref);

        if (payload.callback) payload.callback();
      } catch (err) {
        console.error("Failed to delete schedule item:", err);
        // Optional: rollback or re-fetch could be added here
        if (state.activeEvent?.id === payload.eventId) {
          // e.g., dispatch('fetchSchedule', { docId: payload.eventId })
        }
      }
    },

    // ===== NEW: delete entire event (optionally cascade its schedule) =====
    async deleteEvent(
      { commit, state }: ActionContext<EventsState, RootState>,
      payload: {
        eventId: string;
        cascadeSchedule?: boolean;
        callback?: () => void;
      },
    ): Promise<{ success: boolean; deletedScheduleCount: number }> {
      const { eventId, cascadeSchedule = true } = payload;

      // If we're listening to this event, stop first
      if (state.activeEvent?.id === eventId && state.activeEventUnsub) {
        commit("CLEAR_ACTIVE_EVENT_UNSUB");
      }

      let deletedScheduleCount = 0;

      try {
        // Optionally delete all schedule items in safe batches
        if (cascadeSchedule) {
          const scheduleCol = collection(db, "events", eventId, "schedule");
          const snap = await getDocs(scheduleCol);

          const toDelete = snap.docs.map((d) => d.ref);
          // Firestore batch writes are limited to 500 ops; keep headroom
          const CHUNK_SIZE = 450;
          for (let i = 0; i < toDelete.length; i += CHUNK_SIZE) {
            const batch = writeBatch(db);
            const chunk = toDelete.slice(i, i + CHUNK_SIZE);
            chunk.forEach((ref) => batch.delete(ref));
            await batch.commit();
            deletedScheduleCount += chunk.length;
          }
        }

        // Delete the event doc
        await deleteDoc(doc(db, "events", eventId));

        // Local state cleanup
        if (state.activeEvent?.id === eventId) {
          commit("SET_ACTIVE_EVENT", null);
          commit("CLEAR_ACTIVE_SCHEDULE");
        }
        if (state.upcomingEvent?.id === eventId) {
          commit("SET_UPCOMING_EVENT", null);
        }
        if (state.ongoingEvent?.id === eventId) {
          commit("SET_ONGOING_EVENT", null);
        }
        commit("REMOVE_EVENT_FROM_LIST", eventId);

        if (payload.callback) payload.callback();

        return { success: true, deletedScheduleCount };
      } catch (err) {
        console.error("Failed to delete event:", err);
        commit("SET_ERROR", "Failed to delete event.");
        return { success: false, deletedScheduleCount };
      }
    },

    // ===== Reset =====
    reset({ commit }) {
      commit("CLEAR_ACTIVE_EVENT_UNSUB");
      commit("RESET_STATE");
    },
  },
};

export default teamsModule;
